import { useState, useRef } from 'react'
import QrScanner from 'qr-scanner'
import { X, Camera, Upload, AlertCircle } from 'lucide-react'

interface QRScannerModalProps {
  isOpen: boolean
  onClose: () => void
  onScan: (result: string) => void
  title?: string
}

export function QRScannerModal({ isOpen, onClose, onScan, title = 'Scan QR Code' }: QRScannerModalProps) {
  const [error, setError] = useState<string | null>(null)
  const [scanning, setScanning] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const scannerRef = useRef<QrScanner | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const startScanning = async () => {
    if (!videoRef.current) return

    try {
      setError(null)
      setScanning(true)

      const scanner = new QrScanner(
        videoRef.current,
        (result) => {
          onScan(result.data)
          stopScanning()
          onClose()
        },
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
        }
      )

      scannerRef.current = scanner
      await scanner.start()
    } catch (err) {
      setError('Failed to start camera. Please check permissions.')
      console.error('Scanner error:', err)
      setScanning(false)
    }
  }

  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.stop()
      scannerRef.current.destroy()
      scannerRef.current = null
    }
    setScanning(false)
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      setError(null)
      const result = await QrScanner.scanImage(file, { returnDetailedScanResult: true })
      onScan(result.data)
      onClose()
    } catch (err) {
      setError('No QR code found in image')
      console.error('Scan error:', err)
    }
  }

  const handleClose = () => {
    stopScanning()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(36, 41, 56, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '2px solid rgba(0, 240, 255, 0.4)',
        borderRadius: '12px',
        padding: '24px',
        width: '100%',
        maxWidth: '600px',
        margin: '0 16px',
        fontFamily: 'Rajdhani, sans-serif'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '2px solid rgba(0, 240, 255, 0.3)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(0, 240, 255, 0.2)',
              border: '2px solid #00F0FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Camera style={{ width: 20, height: 20, color: '#00F0FF' }} />
            </div>
            <h3 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '24px',
              fontWeight: 700,
              color: '#00F0FF',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0
            }}>
              {title}
            </h3>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(138, 149, 170, 0.1)',
              border: '2px solid rgba(138, 149, 170, 0.4)',
              borderRadius: '6px',
              padding: '8px',
              color: '#8A95AA',
              cursor: 'pointer'
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '2px solid rgba(239, 68, 68, 0.4)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertCircle style={{ width: 20, height: 20, color: '#EF4444', flexShrink: 0 }} />
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#EF4444',
              margin: 0
            }}>
              {error}
            </p>
          </div>
        )}

        {/* Content */}
        {!scanning ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0
            }}>
              Choose how to scan your QR code:
            </p>
            <button
              onClick={startScanning}
              style={{
                width: '100%',
                background: 'rgba(0, 240, 255, 0.2)',
                border: '2px solid #00F0FF',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#00F0FF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 0 20px rgba(0, 240, 255, 0.3)'
              }}
            >
              <Camera style={{ width: 16, height: 16 }} />
              START CAMERA
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                background: 'rgba(185, 27, 255, 0.1)',
                border: '2px solid rgba(185, 27, 255, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#B91BFF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Upload style={{ width: 16, height: 16 }} />
              UPLOAD IMAGE
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                maxHeight: '400px',
                borderRadius: '8px',
                border: '2px solid #00F0FF',
                background: '#000000',
                boxShadow: '0 0 30px rgba(0, 240, 255, 0.3)'
              }}
            />
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#00F0FF',
              textAlign: 'center',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: 0
            }}>
              Point your camera at a QR code
            </p>
            <button
              onClick={stopScanning}
              style={{
                width: '100%',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '2px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#EF4444',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <X style={{ width: 16, height: 16 }} />
              CANCEL SCANNING
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
